import React, { useState } from 'react';
import { Form } from 'antd';
import { useTranslation } from 'react-i18next';
import { Button, BUTTON_TYPES, Divider, Input, Popup, TextArea, Typography } from '_atoms';
import { ReactComponent as Bin } from 'dist/icons/bin.svg';
import { ReactComponent as Plus } from 'dist/icons/Union.svg';
import { STEPS_LABELS } from 'constants/constants';
import { SUMMARY_POINT_MAX_LENGTH, SUMMARY_TEXT_MAX_LENGTH } from './constants';
import type { SummaryEditProps, SummaryEditValues } from './types';

type EditableListName = 'positive_decisions' | 'risks_concerns';

interface EditableSummaryListProps {
  label: string;
  listName: EditableListName;
}

const EditableSummaryList: React.FC<EditableSummaryListProps> = ({ label, listName }) => {
  const { t } = useTranslation();

  return (
    <Form.List name={listName}>
      {(fields, { add, remove }) => (
        <div className="job-candidate-summary__edit-list-section">
          <Typography.Label>{label}</Typography.Label>
          <div className="job-candidate-summary__edit-list">
            {fields.map(field => (
              <div className="job-candidate-summary__edit-list-row" key={field.key}>
                <Input
                  itemProps={{ ...field }}
                  maxLength={SUMMARY_POINT_MAX_LENGTH}
                  placeholder={t('translation:job_candidate_summary.placeholder_enter_text')}
                />
                <Button
                  aria-label={t('translation:job_candidate_summary.action_remove_point')}
                  className="job-candidate-summary__edit-delete"
                  icon={<Bin />}
                  type={BUTTON_TYPES.TEXT}
                  variant="grey"
                  onClick={() => remove(field.name)}
                />
              </div>
            ))}
          </div>
          <Button
            className="job-candidate-summary__edit-add"
            icon={<Plus />}
            type={BUTTON_TYPES.SECONDARY}
            onClick={() => add('')}
          >
            {t('translation:job_candidate_summary.action_add_point')}
          </Button>
        </div>
      )}
    </Form.List>
  );
};

export const SummaryEdit: React.FC<SummaryEditProps> = ({ summary, stepName, loading, onCancel, onSave }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<SummaryEditValues>();
  const [discardPopupOpen, setDiscardPopupOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const interviewFeedbackDisabled =
    stepName === STEPS_LABELS.CONTACT_WITH_CANDIDATE || stepName === STEPS_LABELS.CV_SCREENING;
  const initialValues: SummaryEditValues = {
    overall_summary: summary.content.overall_summary,
    interview_feedback: summary.content.interview_feedback.map(({ text }) => ({ text })),
    positive_decisions: summary.content.positive_decisions,
    risks_concerns: summary.content.risks_concerns,
  };

  const closeDiscardPopup = () => setDiscardPopupOpen(false);

  const cancelEditing = () => {
    if (hasUnsavedChanges) {
      setDiscardPopupOpen(true);
      return;
    }

    onCancel();
  };

  const discardChanges = () => {
    closeDiscardPopup();
    onCancel();
  };

  return (
    <>
      <Form
        className="job-candidate-summary__edit"
        disabled={loading}
        form={form}
        initialValues={initialValues}
        name="job-candidate-summary-edit"
        onFinish={onSave}
        onValuesChange={() => setHasUnsavedChanges(true)}
      >
        <TextArea
          className="job-candidate-summary__edit-textarea"
          itemProps={{ name: 'overall_summary' }}
          label={t('translation:job_candidate_summary.overall_summary')}
          maxLength={SUMMARY_TEXT_MAX_LENGTH}
        />
        <Divider />
        <Form.List name="interview_feedback">
          {fields => (
            <div className="job-candidate-summary__edit-feedback">
              <Typography.Label>{t('translation:job_candidate_summary.interview_feedback')}</Typography.Label>
              {fields.map((field, index) => (
                <TextArea
                  className="job-candidate-summary__edit-textarea"
                  disabled={loading || interviewFeedbackDisabled}
                  itemProps={{ ...field, name: [field.name, 'text'] as unknown as string }}
                  key={field.key}
                  label={summary.content.interview_feedback[index]?.title || ''}
                  maxLength={SUMMARY_TEXT_MAX_LENGTH}
                />
              ))}
            </div>
          )}
        </Form.List>
        <Divider />
        <EditableSummaryList
          label={t('translation:job_candidate_summary.positive_decisions')}
          listName="positive_decisions"
        />
        <Divider />
        <EditableSummaryList label={t('translation:job_candidate_summary.risks_concerns')} listName="risks_concerns" />
        <Divider />
        <div className="job-candidate-summary__edit-footer">
          <Button htmlType="button" type={BUTTON_TYPES.SECONDARY} variant="grey" onClick={cancelEditing}>
            {t('translation:job_candidate_summary.action_cancel')}
          </Button>
          <Button htmlType="submit" type={BUTTON_TYPES.PRIMARY} loading={loading}>
            {t('translation:job_candidate_summary.action_save')}
          </Button>
        </div>
      </Form>
      <Popup
        open={discardPopupOpen}
        title={t('translation:job_candidate_summary.discard_title')}
        onCancel={closeDiscardPopup}
        onClose={closeDiscardPopup}
        cancelButtonProps={{
          onClick: closeDiscardPopup,
          children: t('translation:job_candidate_summary.action_back_to_editing'),
          variant: 'grey',
        }}
        okButtonProps={{
          onClick: discardChanges,
          children: t('translation:job_candidate_summary.action_discard'),
        }}
      >
        <Typography.Text className="job-candidate-summary__discard-message">
          {t('translation:job_candidate_summary.discard_message')}
        </Typography.Text>
      </Popup>
    </>
  );
};
